import React, { useContext, useRef } from 'react';
import {
  ActiveCompanyModuleEnum,
  GetLinkedCompaniesFilterInDTO,
  GetLinkedCompanyOutDTO,
  Pageable,
} from '@api/ringil-api.v2';
import { ApiContext } from '@api/api';
import useNavigation from '@hooks/useNavigation.tsx';
import { Link } from 'react-router-dom';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import clsx from 'clsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { useMediaQuery } from 'react-responsive';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import { CarrierReferrer } from '@app/pages/customer/carriers/carrier-detail/CarrierDetail.tsx';

const AllCarriers: React.FC = () => {
  const { tr } = useTranslationLgs();
  const { getCarrierList } = useContext(ApiContext);

  const navigation = useNavigation();
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const loadDataApi = (filter: GetLinkedCompaniesFilterInDTO, pageable: Pageable, onSuccess: (pagedData: PagedData<GetLinkedCompanyOutDTO>) => void) => {
    getCarrierList(
      filter,
      pageable,
      data => {
        onSuccess(data);
      },
    );
  };

  const nameBodyTemplate = (rowData: GetLinkedCompanyOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <Link
          to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierDetail(rowData.companyId, CarrierReferrer.PARTNERS))}
        >
            <span
              className={'text-[16px] font-normal leading-normal tracking-[0.24px] text-content-brand font-dm-sans'}
            >
              {rowData.companyName}
            </span>
        </Link>
      </div>
    );
  };
  const modulesBodyTemplate = (rowData: GetLinkedCompanyOutDTO) => {
    return (
      <div className="flex gap-2 items-center min-h-10">
        {(rowData.activeCompanyModule ?? [])
          .map(module => tr('CompanyModule.' + module, module))
          .sort((a, b) => a.localeCompare(b))
          .join(', ')}
      </div>
    );
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr(`AllCarriersList.searchName`, `Search name`),
      value: ['companyName'],
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr(`AllCarriersList.searchModules`, `Search modules`),
      value: ['companyModules'],
      options: Object.values(ActiveCompanyModuleEnum).map(module => ({
        label: tr('CompanyModule.' + module, module),
        value: module,
        active: true,
      }))
    },
  ];

  const columnsConfig: ColumnConfig<GetLinkedCompanyOutDTO>[] = [
    {
      id: 'carrierName',
      header: 'AllCarriersList.name',
      headerDefault: 'Name',
      width: '50%',
      sortable: true,
      field: 'name',
      order: 1,
      render: nameBodyTemplate,
    },
    {
      id: 'carrierModules',
      header: 'AllCarriersList.modules',
      headerDefault: 'Modules',
      width: '50%',
      sortable: false,
      order: 2,
      render: modulesBodyTemplate,
    },
  ];

  return (
    <>
      <div className={clsx('font-[\'DM Sans\']', isMobile && '-mx-4 px-8 pt-8')}>
        <div className="flex items-center mb-6">
          <COHeadline title={tr(`AllCarriersList.titleCarriers`, `All carriers`)} variant={'h1'} />
        </div>
      </div>

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig as ColumnConfig<unknown>[]}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'settings_carriers_list'}
      />

    </>
  );
};

export default AllCarriers;
